import React, { ReactElement, forwardRef, useImperativeHandle, useState, useMemo } from 'react';
import { KView, KText, PageContext } from '@/component/RtlLayout';
import { TouchableWithoutFeedback } from 'react-native';
import { convertStyles } from '@kibt/krn-utils';
import { getDomain } from '@/common/api';
import { openUrl } from '@/common/bridge/openUrl';
import { useToast } from '@/component/Toast/Toast';
import dayjs from 'dayjs';

import SrcStyles from './index.styl';
import { $k18n } from '@/common/i18n';
const Styles = convertStyles(SrcStyles);

import { postClaimCoupon } from './api';
import { isIOS } from '@/common/device-info';
import { lang } from '@/common/const';

import { goToCouponDetail } from '../../../jump';
import { logClickDetailCouponCard, logShowDetailCouponCard } from '../../../logger2.0';
import { Model } from '@/request';
import { ViewVisible } from '@/component/ViewVisible/ViewVisible';
import { DetailShopInfo, COUPON_RANGE_TYPE } from '../../types';
import { jumpToMerchant } from '@/common/biz/jump';
import { MearchantSourceType } from '@/common/merchant-source-enums';
import { ThemeColorMap, useCouponPush } from '@/hooks/abTest/useCouponPush';
import { useCouponMakeup } from '@/hooks/abTest/useCouponMakeup';
import { useChannelCouponTag } from '../../utils';
import { useQuery } from '@/hooks/useQuery';

const LOCALE = lang || 'en';

// "discountCoupon": [{
//     "couponId": 10901604,
//     "batchId": 8701,
//     "couponState": 1,
//     "name": "修改列表-新ab实验版本-券批次1-无ab实验",
//     "discountType": 1,
//     "discountFeeType": 3,
//     "maxDeductAmount": 700,
//     "displayMaxDeductAmount": "R$7,00",
//     "expireTime": 1685414868427,
//     "expiredPeriodType": 4,
//     "expiredPeriodValue": 7
// }],
type CounponItem = Model['POST/rest/o/ecommerce/marketing/c/customer/coupon/shop/cart/list']['responses']['data'][number];
type IProps = {
    updateMiniCouponInfo: any; // 刷新商详页优惠券相关信息
    coupon: CounponItem; // 类型
    disableJump: boolean; // 优惠券区域点击是否允许跳转到推荐列表
    disableClaim: boolean; // 禁止领取优惠券
    shopInfo?: DetailShopInfo; // 店铺信息
    itemInfo?: any; // 商品信息
    isDetailCouponToMerchant?: boolean;
    onClaimSuccess?: (item: CounponItem) => void;
    onClaimClick?: () => void;
};

const CouponCardLarge = forwardRef(function specLine(props: IProps, ref): ReactElement {
    const { coupon, updateMiniCouponInfo, isDetailCouponToMerchant, itemInfo } = props;
    const disableJump = props.disableJump || false;
    const disableClaim = props.disableClaim || false;

    const { showToast, toastNode } = useToast();
    const [couponInfo, setCouponInfo] = useState<{ claimQuantity?: number; [key: string]: any }>(coupon);

    const { couponPush } = useCouponPush();
    const { couponMakeup } = useCouponMakeup();

    const themeColor = couponPush.couponUsePrompt && coupon.discountFeeType === 3 ? ThemeColorMap.shipping : ThemeColorMap.product;

    const formatLogisticsTime = (time: number) => {
        if (LOCALE === 'pt-BR') {
            return dayjs(Number(time)).format('MM/DD/YYYY HH:mm');
        }
        return dayjs(Number(time)).format('YYYY/MM/DD HH:mm');
    };

    // 优惠券门槛展示文案
    const couponThreshold = () => {
        if (couponInfo.threshold) {
            return $k18n({ id: 'k_322825', params: { min_spend: couponInfo.displayThreshold } });
        }
        return $k18n({ id: 'k_322811', text: '无门槛' });
    };

    //
    const couponDescription = () => {
        if (couponInfo.firstOrderLimit) {
            return $k18n({ id: 'k_445500', text: '首单可用' });
        }

        if (couponInfo.discountRangeType === 0) {
            return $k18n({ text: '大部分商品可用' });
        }

        if (couponInfo.discountRangeType === 2) {
            return $k18n({ text: '指定店铺可用' });
        }

        if (couponInfo.discountRangeType === 3) {
            return $k18n({ text: '指定商品品类可用' });
        }

        if (couponInfo.discountRangeType === 1) {
            return $k18n({ id: 'k_435825', text: '指定商品可用' });
        }

        return $k18n({ text: '大部分商品可用' });
    };

    // 优惠券类型展示文案，discountFeeType === 3为运费券， discountFeeType === 2为商品券
    const couponType = () => {
        if (couponInfo.discountFeeType === 3) {
            return $k18n({ id: 'k_322802', text: '运费' });
        }

        if (couponInfo.discountFeeType === 2) {
            return $k18n({ id: 'product_coupon_tag', text: '商品' });
        }

        return $k18n({ id: 'k_322802', text: '运费' });
    };

    // 优惠券减免金额文案
    const couponAmount = () => {
        if (couponInfo.discountFeeType === 3) {
            return $k18n({ id: 'free_shipping_upper', text: '包邮' });
        } else return $k18n({ id: 'k_322805', params: { discount: couponInfo.displayMaxDeductAmount } });
    };

    // 特殊场域优惠卷文案
    // const couponChannel = () => {
    //     if (couponInfo.currentChannelType === 3) {
    //         return $k18n({ id: '474806' })
    //     }
    //     else if (couponInfo.currentChannelType === 6 || couponInfo.currentChannelType === 9) {
    //         return $k18n({ id: 'k_474807' })
    //     }
    // }
    const couponChannelText = useChannelCouponTag(couponInfo.currentChannelType);

    // 减免文案
    const couponDiscountText = () => {
        if (couponInfo.discountFeeType === 3)
            return `; ${$k18n({ id: 'max_discount', params: { max_discount: couponInfo.displayMaxDeductAmount } })}`;
    };

    // 跳转到优惠券适用商品列表页
    const goToUse = () => {
        if (disableJump) return;
        const { couponId, batchId } = couponInfo;
        // const url = stringifyUrl(`${window.location.origin}/shop/c/coupon/reco-list`, {
        //     couponId,
        //     batchId,
        //     __launch_options__: JSON.stringify({
        //         topBarPosition: 'fixed',
        //     }),
        //     hyId: 'kibt-fe-shop-c-v1',
        // });
        // openKwaiH5({ url });
        const url =
            getDomain() +
            `/shop/c/coupon/reco-list?hyIdkibt-fe-shop-c-v1&couponId=${couponId}&batchId=${batchId}&__launch_options__=${encodeURIComponent(
                JSON.stringify({
                    topBarPosition: 'fixed',
                }),
            )}`;
        openUrl({ url });
    };

    const goToMakeup = () => {
        if (disableJump) return;

        const { couponId, batchId } = couponInfo;
        const url =
            getDomain() +
            `/shop/c/coupon/make-up?hyId=kibt-fe-shop-c-v1&itemId=${
                itemInfo?.itemId
            }&couponId=${couponId}&batchId=${batchId}&__launch_options__=${encodeURIComponent(
                JSON.stringify({
                    topBarPosition: 'fixed',
                }),
            )}`;
        openUrl({ url });
    };

    const pageType = useQuery<string>('pageType');

    const goToMerChant = () => {
        if (disableJump) return;

        const isToBDetail = pageType === 'seller';
        const { shopInfo } = props;

        jumpToMerchant({
            merchantId: shopInfo?.merchantId,
            isToBDetail,
            extraInfoType: MearchantSourceType.RNDetailCouponHalfPageClickUse,
        });
    };

    const claimCoupon = async () => {
        if (disableClaim) return;
        const { batchId } = couponInfo;

        // claimSelfBuildCoupon({
        //     batchId,
        // }).then(res => {
        //     if (res.result === 1) {
        //         Toast.show({ text: $k18n('领取成功') });
        //         // 领券成功后更新couponId,couponState,expireTime
        //         state.couponInfo.couponId = res.data.couponId;
        //         state.couponInfo.couponState = 1;
        //         state.couponInfo.expireTime = res.data.expireTime;
        //         // 发送事件通知父组件, 父组件外层更新couponList，用于下单时判断是否要自动领取优惠券
        //         emit('claim-coupon', state.couponInfo.couponId);
        //     }
        // });
        props?.onClaimClick?.();
        try {
            const res = await postClaimCoupon({ batchId });
            console.log('res', res);
            if (res.result === 1) {
                showToast({
                    text: $k18n('领取成功'),
                    duration: 1000,
                });
                // 领券成功后更新couponId,couponState,expireTime
                const newInfo = {
                    ...couponInfo,
                    couponId: res.data.couponId,
                    couponState: 1,
                    expireTime: res.data.expireTime,
                };
                setCouponInfo(newInfo);
                props?.onClaimSuccess?.(newInfo);
                // 发送事件通知父组件, 父组件外层更新couponList，用于下单时判断是否要自动领取优惠券
                // emit('claim-coupon', state.couponInfo.couponId);
                // 更新商详页优惠券相关的信息
                updateMiniCouponInfo();
            } else {
                // showToast({
                //     text: (res as any).message,
                //     duration: 3000,
                // });
            }
        } catch (error) {
            // showToast({
            //     text: (error as any).message,
            //     duration: 3000,
            // });
        }
    };

    const handleUseCoupon = () => {
        const { couponState, discountRangeType } = couponInfo;
        logClickDetailCouponCard({
            coupon_amt: couponInfo.displayMaxDeductAmount,
            coupon_id: couponInfo.batchId,
            coupon_type: couponInfo.discountFeeType,
            coupon_status: couponInfo.couponState,
        });
        // 未领取，点击btn发起领劵请求，已领取，点击btn去优惠券适用商品列表页
        if (couponState === 1) {
            // 店铺在售商品数量
            const shopCommodityCount = props.shopInfo?.commodityCount || 0;
            // 是否是店铺券或者平台券
            const isPlatformCouponOrShopCoupon = [COUPON_RANGE_TYPE.AllItem, COUPON_RANGE_TYPE.Shop].includes(discountRangeType);
            // 店铺在售商品数量大于等于 50 且是店铺券或者平台券
            const shouldGoToMerchant = shopCommodityCount >= 50 && isPlatformCouponOrShopCoupon && isDetailCouponToMerchant;
            // 店铺在售商品数小于50 且是店铺券或者平台券 且命中实验
            const shouldGoToMakeup = couponMakeup.couponUsePage && shopCommodityCount < 50 && isPlatformCouponOrShopCoupon;

            if (shouldGoToMerchant) {
                goToMerChant();
            } else if (shouldGoToMakeup) {
                goToMakeup();
            } else {
                goToUse();
            }
        } else if (!couponState) {
            claimCoupon();
        }
    };

    const onGoToCouponDetail = () => {
        goToCouponDetail(couponInfo);
    };

    const getFormatLogisticsTime = () => {
        const { couponState, expiredPeriodType, expiredPeriodValue, expireTime } = couponInfo;

        const expireTypeMap = {
            2: $k18n({ id: 'k_251526', text: '分' }),
            3: $k18n({ id: 'k_251525', text: '时' }),
            4: $k18n({ id: 'k_251524', text: '天' }),
            // 5: $k18n({ id: '', text: '周' }),
            // 6: $k18n({ id: '', text: '月' }),
            // 7: $k18n({ id: '', text: '年' }),
            8: $k18n({ id: 'k_407848', text: '今日有效' }),
            9: $k18n({ id: 'k_407849', text: '本周有效' }),
            10: $k18n({ id: 'k_407850', text: '本月有效' }),
        };

        // 两种展示过期时间方式，1：展示领券后xxx天/小时/分钟后过期；2：固定YYYY/MM/DD HH:mm时间过期
        if (!couponState && expiredPeriodType > 0) {
            // 未领券，并且expiredPeriodType类型大于0（2分钟，3小时，4，天）
            const text = expireTypeMap[expiredPeriodType] || '';
            if ([8, 9, 10].includes(expiredPeriodType || '')) {
                return `${text}`;
            }
            return $k18n({ id: 'k_322828', params: { time: `${expiredPeriodValue}${text}` } });
        }
        return `${$k18n({ id: 'k_322827', text: '失效时间：' })}${formatLogisticsTime(expireTime)}`;
    };

    const getBtnText = () => {
        const { couponState } = couponInfo;

        if (couponState === 1) {
            if (disableJump) {
                return $k18n({ id: 'k_332860', text: '已领取' });
            }
            return $k18n({ id: 'k_322776', text: '使用' });
        }
        if (couponState === 3 || couponState === 2) {
            return $k18n('已使用');
        }
        if (couponState === 4 || couponState === 10) {
            return $k18n('已失效');
        }
        return $k18n({ id: 'k_322774', text: '领取' });
    };

    useImperativeHandle(
        ref,
        () => {
            return {
                // 暴露出去的方法,
            };
        },
        [],
    );

    const couponGrayStyle = useMemo(() => {
        return [0, 2, 3, 4, 10].includes(couponInfo.couponState);
    }, [couponInfo]);

    return (
        <PageContext.Provider value={{ Styles }}>
            <KView rtlClassName={['coupon-wrap']} style={{ borderColor: themeColor.border, backgroundColor: themeColor.background }}>
                <KView rtlClassName={'left'}>
                    {/* 特殊ui */}
                    {couponChannelText ? (
                        <KView rtlClassName={'tag-box'}>
                            <KView rtlClassName={'tag'} style={{ backgroundColor: themeColor.border }}>
                                <KText style={{ color: themeColor.text, fontSize: 12, lineHeight: 16 }}>{couponChannelText}</KText>
                            </KView>
                        </KView>
                    ) : null}
                    {couponPush.couponUsePrompt ? null : (
                        <KView rtlClassName={'top'}>
                            <KView rtlClassName={['type']}>
                                <KText rtlClassName={['type-text']}>{couponType()}</KText>
                            </KView>
                        </KView>
                    )}
                    <KView rtlClassName={'price'}>
                        <KText
                            rtlClassName={['price-text', couponInfo.couponState && couponInfo.couponState !== 1 ? 'coupon-disabled-price-text' : '']}
                            style={{ color: themeColor.text }}
                        >
                            {couponAmount()}
                        </KText>
                    </KView>
                    <KView rtlClassName={'title'}>
                        <KText
                            rtlClassName={['title-text', couponInfo.couponState && couponInfo.couponState !== 1 ? 'coupon-disabled-title-text' : '']}
                            style={{
                                fontSize: 16,
                            }}
                        >
                            {/* {$k18n({ id: 'k_322811', text: '无门槛' })} */}
                            {couponThreshold()}
                            {couponDiscountText()}
                        </KText>
                    </KView>
                    <KView rtlClassName={'desc'}>
                        <KText rtlClassName={['desc-text', couponInfo.couponState !== 1 ? 'coupon-disabled-desc-text' : '']}>
                            {/* {$k18n({ id: 'k_322826', text: '大部分商品可适用' })} */}
                            {couponDescription()}
                        </KText>
                    </KView>
                    <KView rtlClassName={'expire'}>
                        <KText
                            rtlClassName={['desc-text', couponInfo.couponState && couponInfo.couponState !== 1 ? 'coupon-disabled-desc-text' : '']}
                        >
                            {getFormatLogisticsTime()}
                        </KText>
                        <KView rtlClassName={'divider'} />
                        <TouchableWithoutFeedback onPress={() => onGoToCouponDetail()}>
                            <KText
                                rtlClassName={['tc-text', couponInfo.couponState && couponInfo.couponState !== 1 ? 'coupon-disabled-tc-text' : '']}
                            >
                                {$k18n({ id: 'k_322777', text: '条款' })}
                            </KText>
                        </TouchableWithoutFeedback>
                    </KView>
                </KView>
                <KView rtlClassName={'dash-wrap'}>
                    <KView rtlClassName={'before'} />
                    <KView rtlClassName={'circle'} style={{ borderColor: themeColor.border }} />
                    {/* todo borderLeftWidth 不生效 */}
                    <KView
                        rtlClassName={'dash'}
                        style={{
                            borderRadius: 1,
                            borderLeftWidth: 1,
                            borderColor: themeColor.border,
                            borderStyle: isIOS ? 'solid' : 'dotted',
                        }}
                    />
                    <KView rtlClassName={'circle'} style={{ borderColor: themeColor.border }} />
                    <KView rtlClassName={'after'} />
                </KView>
                <ViewVisible
                    onVisible={() =>
                        logShowDetailCouponCard({
                            coupon_amt: couponInfo.displayMaxDeductAmount,
                            coupon_id: couponInfo.batchId,
                            coupon_type: couponInfo.discountFeeType,
                            coupon_status: couponInfo.couponState,
                        })
                    }
                >
                    <TouchableWithoutFeedback onPress={() => handleUseCoupon()}>
                        <KView rtlClassName={'right'}>
                            {typeof couponInfo.claimQuantity === 'number' && couponInfo.claimQuantity > 1 ? (
                                <KView
                                    rtlClassName={'count'}
                                    style={{
                                        borderTopRightRadius: 8,
                                        borderBottomLeftRadius: 8,
                                        borderWidth: 0,
                                        backgroundColor: themeColor.cornorBackground,
                                    }}
                                >
                                    <KText
                                        rtlClassName={'count-text'}
                                        numberOfLines={1}
                                        ellipsizeMode={'tail'}
                                        style={{
                                            color: themeColor.text,
                                        }}
                                    >
                                        x{couponInfo.claimQuantity}
                                    </KText>
                                </KView>
                            ) : null}
                            <KView
                                rtlClassName={[[1, 2, 3, 4, 10].includes(couponInfo.couponState) ? 'use-coupon-btn-to-use' : 'use-coupon-btn']}
                                style={
                                    [1, 2, 3, 4, 10].includes(couponInfo.couponState)
                                        ? {
                                              borderColor: themeColor.btnBorder,
                                              borderWidth: couponPush.couponUsePrompt ? 1 : 0,
                                          }
                                        : {
                                              backgroundColor: themeColor.text,
                                          }
                                }
                            >
                                <KText
                                    rtlClassName={[
                                        [1, 2, 3, 4, 10].includes(couponInfo.couponState) ? 'use-coupon-btn-to-use-text' : 'use-coupon-btn-text',
                                    ]}
                                    style={[1, 2, 3, 4, 10].includes(couponInfo.couponState) ? { color: themeColor.text } : { color: '#fff' }}
                                >
                                    {getBtnText()}
                                </KText>
                            </KView>
                        </KView>
                    </TouchableWithoutFeedback>
                </ViewVisible>
            </KView>
            {toastNode}
        </PageContext.Provider>
    );
});
export default CouponCardLarge;
